import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { consentSchema, type ConsentInput, getFullName, calculateAge } from 'shared'
import Checkbox from '../ui/Checkbox'
import Button from '../ui/Button'
import type { PatientRecord } from 'shared'

interface Step4Props {
  formData: Partial<PatientRecord>
  onComplete: (data: Partial<PatientRecord>) => void
  onBack: () => void
}

const Step4Consent = ({ formData, onComplete, onBack }: Step4Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConsentInput>({
    mode: 'onBlur',
    resolver: zodResolver(consentSchema),
  })

  const onSubmit = (data: ConsentInput) => {
    onComplete({
      consent: {
        ...data,
        consentedAt: new Date(),
      },
    })
  }

  const basicInfo = formData.basicInfo
  const fullName = basicInfo
    ? getFullName(basicInfo.lastNameKanji, basicInfo.firstNameKanji)
    : ''

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-5xl mx-auto">
      <h2 className="text-xl font-medium text-neutral-800 mb-6">確認・同意</h2>

      {/* 入力内容確認 */}
      <div className="mb-8 p-5 bg-white rounded-lg border border-neutral-300">
        <h3 className="text-base font-medium text-neutral-800 mb-4">入力内容の確認</h3>

        <dl className="space-y-3 text-sm">
          <div className="flex border-b border-neutral-200 pb-2">
            <dt className="w-32 text-neutral-600">お名前</dt>
            <dd className="text-neutral-900 flex-1">{fullName}</dd>
          </div>
          <div className="flex border-b border-neutral-200 pb-2">
            <dt className="w-32 text-neutral-600">フリガナ</dt>
            <dd className="text-neutral-900 flex-1">
              {basicInfo
                ? getFullName(basicInfo.lastNameKana, basicInfo.firstNameKana)
                : ''}
            </dd>
          </div>
          <div className="flex border-b border-neutral-200 pb-2">
            <dt className="w-32 text-neutral-600">電話番号</dt>
            <dd className="text-neutral-900 flex-1">{basicInfo?.phoneNumber}</dd>
          </div>
          <div className="flex border-b border-neutral-200 pb-2">
            <dt className="w-32 text-neutral-600">性別</dt>
            <dd className="text-neutral-900 flex-1">{basicInfo?.gender}</dd>
          </div>
          <div className="flex border-b border-neutral-200 pb-2">
            <dt className="w-32 text-neutral-600">生年月日</dt>
            <dd className="text-neutral-900 flex-1">
              {basicInfo?.birthday && `${basicInfo.birthday} (${calculateAge(basicInfo.birthday)}歳)`}
            </dd>
          </div>
          <div className="flex">
            <dt className="w-32 text-neutral-600">来店きっかけ</dt>
            <dd className="text-neutral-900 flex-1">{formData.details?.visitSource}</dd>
          </div>
        </dl>
      </div>

      {/* 免責同意書・個人情報取り扱い */}
      <div className="mb-6 p-5 bg-white border border-neutral-300 rounded-lg max-h-96 overflow-y-auto">
        <h3 className="text-base font-medium text-neutral-800 mb-3">免責同意書</h3>
        <div className="text-sm text-neutral-700 space-y-3">
          <p>当店は国家資格を必要としないリラクゼーションを目的として運営しております。治療を目的とした医療（類似）行為は一切行っておりません。</p>

          <p>以下の項目に当てはまる場合の施術はお断り、中断する可能性がございますので予めご了承下さい。</p>
          <ul className="list-disc list-inside space-y-1 ml-4 text-xs leading-relaxed">
            <li>妊娠されている方</li>
            <li>飲酒、多量の食事を摂った直後の方</li>
            <li>半年以内に手術をされた方、著しく体力が低下している方</li>
            <li>極度の日焼け、伝染性皮膚炎、重度の水虫、皮膚に炎症のある方</li>
            <li>怪我、捻挫、打撲、火傷、炎症、肉離れ、ヘルニアなどのある方</li>
            <li>発熱、ウイルス性の病気、伝染病などの方、現在治療中の方</li>
            <li>24時間以内に注射を受けられた方</li>
            <li>心臓病、てんかん症、骨粗しょう症、狭心症、心筋梗塞、高血圧、糖尿病、人工透析をうけているなどのご病気の方</li>
            <li>脳出血、脳血栓をおこした方</li>
            <li>悪性腫瘍、動脈癌、動脈硬化、静脈癌のある方</li>
            <li>アザのできやすい体質の方</li>
            <li>店で定められた施術以外の強要、強制をする方</li>
            <li>その他当店がふさわしくないと判断した場合</li>
          </ul>

          <p>また、ご本人の判断で受けて頂いた場合、お客様の要望で強い力を加えた場合の施術による悪影響に関して店では一切責任を負いかねますので自己責任でご要望ださい。</p>

          <p>万一、施術中にお身体、体調の異常や強い痛みを感じた際にはその場でお申しつけください。</p>

          <p>途中退室頂く場合、施術後に発症した傷病や、医師による因果関係の証明不能な傷病については、いかなる場合も返金・賠責には一切応じられません。</p>

          <p>店内、駐車場でのトラブルやお預かり品のトラブルは一切責任を負いかねます。</p>
        </div>

        <h3 className="text-base font-medium text-neutral-800 mt-6 mb-3">個人情報利用について</h3>
        <div className="text-sm text-neutral-700 space-y-3">
          <p>当店では、個人情報保護法及び関連法規を遵守するとともに、店の方針に基づき個人情報の保護に努めてまいります。</p>

          <p>店が個人情報を取得する場合は、各種法令を遵守し、公正な手段で個人情報を取得いたします。</p>

          <p>当店が取得した個人情報につきましては、利用目的により常識的に関連性のある範囲内で利用いたします。</p>

          <p>また、個人情報を第三者に提供する場合は、法令に基づき提出する場合、裁判所の命令による場合、または訴訟手続による場合を除き、本人の同意なしに提供はいたしません。</p>
        </div>
      </div>

      {/* 同意チェックボックス */}
      <div className="mb-6">
        <Checkbox
          {...register('agreedToTerms')}
          label="上記の内容を確認し、承諾のうえ施術を申し込みます。"
          error={errors.agreedToTerms?.message}
          required
        />
      </div>

      <div className="mt-8 flex justify-between gap-4">
        <Button type="button" variant="secondary" size="large" onClick={onBack} className="min-w-[140px]">
          戻る
        </Button>
        <Button type="submit" variant="primary" size="large" className="min-w-[200px]">
          送信する
        </Button>
      </div>
    </form>
  )
}

export default Step4Consent
